import { useInfiniteQuery } from '@tanstack/react-query';

import {
  discoverySearchResultsInfiniteQueryOptions,
  type Creator,
  type DiscoverySearchResultsRequest,
} from '@/entities/discovery';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

type Options = {
  enabled?: boolean;
  initialAttempt?: number;
};

export function useDiscoverySearchResults(
  discoverySessionId: string | null | undefined,
  searchId: string | null | undefined,
  request: DiscoverySearchResultsRequest = {},
  options?: Options,
) {
  const { workspaceId } = useWorkspaceId();
  const sessionId = discoverySessionId ?? '';
  const resolvedSearchId = searchId ?? '';
  const initialAttempt = options?.initialAttempt ?? 1;
  const query = useInfiniteQuery({
    ...discoverySearchResultsInfiniteQueryOptions(
      workspaceId,
      sessionId,
      resolvedSearchId,
      { show_saved: request.show_saved },
      initialAttempt,
    ),
    enabled: sessionId.length > 0 && resolvedSearchId.length > 0 && (options?.enabled ?? true),
  });
  const allCreators = query.data?.pages.flatMap((page) => page.creators) ?? [];
  const creators = filterDiscoveryCreatorsByRequiredFeatures(
    allCreators,
    request.required_post_features,
  );

  return {
    ...query,
    creators,
    attemptCount: query.data?.pages.length ?? 0,
    isComplete: !query.hasNextPage && query.isSuccess,
    stats: {
      total: query.data?.pages.at(-1)?.total_count ?? 0,
      fullMatched: creators.length,
    },
  };
}

export function filterDiscoveryCreatorsByRequiredFeatures(
  creators: Creator[],
  requiredFeatures: string[] | null | undefined,
) {
  const normalizedRequiredFeatures = normalizeFeatureSet(requiredFeatures);
  if (normalizedRequiredFeatures.size === 0) return creators;

  return creators.filter((creator) => {
    const normalizedMatchedFeatures = normalizeFeatureSet(creator.matched_keywords);
    return Array.from(normalizedRequiredFeatures).every((feature) =>
      normalizedMatchedFeatures.has(feature),
    );
  });
}

function normalizeFeatureSet(features: string[] | null | undefined) {
  return new Set(
    (features ?? [])
      .map((feature) => feature.trim().toLowerCase())
      .filter((feature) => feature.length > 0),
  );
}
